import React from 'react';
import { Card, Table, Badge } from 'react-bootstrap';
import { formatCurrency } from '../utils/pricingCalculations';

/**
 * Visual component that shows a detailed price calculation breakdown.
 *
 * Shows how the final price is calculated from cost components:
 * COGS + Fulfillment Cost + Additional Fees = Total Cost
 * Then applies margin formula: Price = Total Cost / (1 - Margin%)
 *
 * @param {object} props
 * @param {object} props.breakdown - Combined breakdown from calculateTotalCost + calculateFromMargin
 *   Expected shape: { cogs, fulfillmentCost, fulfillmentSource, additionalFees, totalCost,
 *                     price, marginAmount, marginPercent, markupPercent }
 * @param {boolean} props.compact - If true, show single-line compact view (default: false)
 */
export function PriceBreakdownCard({ breakdown, compact = false }) {
  if (!breakdown) {
    return null;
  }

  const {
    cogs = 0,
    fulfillmentCost = 0,
    fulfillmentSource = 'fallback',
    additionalFees = 0,
    totalCost = 0,
    price = 0,
    marginAmount = 0,
    marginPercent = 0,
    markupPercent = 0,
  } = breakdown;

  // Get badge variant based on fulfillment source
  const getBadgeVariant = (source) => {
    switch (source) {
      case 'fcOptimizer':
        return 'success';
      case 'weightEstimate':
        return 'info';
      case 'fallback':
      default:
        return 'warning';
    }
  };

  // Get friendly label for fulfillment source
  const getSourceLabel = (source) => {
    switch (source) {
      case 'fcOptimizer':
        return 'FC Optimizer';
      case 'weightEstimate':
        return 'Weight Est.';
      case 'fallback':
      default:
        return 'Fallback';
    }
  };

  // Compact view: single line summary
  if (compact) {
    return (
      <div className="d-inline-flex align-items-center gap-2">
        <strong>{formatCurrency(price)}</strong>
        <small className="text-muted">
          (COGS: {formatCurrency(cogs)} + FC: {formatCurrency(fulfillmentCost)}
          {additionalFees > 0 && ` + Fees: ${formatCurrency(additionalFees)}`}
          {' + '}{marginPercent}% margin)
        </small>
        <Badge bg={getBadgeVariant(fulfillmentSource)} className="ms-1">
          {getSourceLabel(fulfillmentSource)}
        </Badge>
      </div>
    );
  }

  // Full view: detailed card with table
  return (
    <Card className="price-breakdown-card">
      <Card.Header className="py-2">
        <strong>Price Calculation</strong>
      </Card.Header>
      <Card.Body className="p-0">
        <Table size="sm" className="mb-0">
          <tbody>
            <tr>
              <td>Product Cost (COGS)</td>
              <td className="text-end">{formatCurrency(cogs)}</td>
            </tr>
            <tr>
              <td>
                Fulfillment Cost
                <Badge bg={getBadgeVariant(fulfillmentSource)} className="ms-2">
                  {getSourceLabel(fulfillmentSource)}
                </Badge>
              </td>
              <td className="text-end">{formatCurrency(fulfillmentCost)}</td>
            </tr>
            {additionalFees > 0 && (
              <tr>
                <td>Additional Fees</td>
                <td className="text-end">{formatCurrency(additionalFees)}</td>
              </tr>
            )}
            <tr className="table-secondary">
              <td><strong>Total Cost Basis</strong></td>
              <td className="text-end"><strong>{formatCurrency(totalCost)}</strong></td>
            </tr>
            <tr>
              <td>Target Margin ({marginPercent}%)</td>
              <td className="text-end">+ {formatCurrency(marginAmount)}</td>
            </tr>
            <tr className="table-success">
              <td><strong>Final Price</strong></td>
              <td className="text-end"><strong>{formatCurrency(price)}</strong></td>
            </tr>
          </tbody>
        </Table>
      </Card.Body>
      <Card.Footer className="text-muted small py-2">
        <div>Using margin formula: Price = Cost / (1 - Margin%)</div>
        <div>Equivalent markup: {markupPercent}%</div>
      </Card.Footer>
    </Card>
  );
}

export default PriceBreakdownCard;
